import {
  Outlet,
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { ChevronRight, XIcon } from "lucide-react";
import Button from "../../components/core/button.core";
import { useGetBrandCampaignByIdHttp } from "../../hooks/http/campaigns/get-brand-campaign-by-id.hook";
import { handleHttpError } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";
import { useEffect, useState } from "react";
import type { IBrandCampaign } from "../../hooks/http/campaigns/get-brand-campaigns.hook";
import Spinner from "../../../components/spinner.components";
import SearchIcon from "../../../assets/svgs/common/search.svg";
import Input from "../../components/core/input.core";
// import { MilestoneRewardType } from "../../../z/brand/campaign-form.brand.z";

export default function CampaignPartial() {
  const [campaignData, setCampaignData] = useState<IBrandCampaign>();

  const menuList = [
    {
      name: "Metrics",
      link: "metrics",
    },
    {
      name: "Live",
      link: "live",
    },
    {
      name: "Post",
      link: "post",
    },
    {
      name: "Draft",
      link: "draft",
    },
    //  ...( (campaignData?.milestoneRewardType === MilestoneRewardType.PRODUCT || campaignData?.milestoneRewardType === MilestoneRewardType.CASH_PRODUCT) ? [{
    //     name: "Shipments",
    //     link: "shipments",
    //   }] : [])
  ];

  const { pathname } = useLocation();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [searchQuery] = useSearchParams();

  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState(searchQuery.get("query") || "");
  const [previousPath, setPreviousPath] = useState<string>("");

  const { toast } = useToast();

  const { execute: getBrandCampaignByIdHttp, fetching: gettingBrandCampaign } =
    useGetBrandCampaignByIdHttp();

  const isActive = (path: string) => {
    if (pathname.includes(path)) {
      return true;
    }
  };

  async function getBrandCampaignById(campaignId: string) {
    const response = await getBrandCampaignByIdHttp({
      body: null,
      pathParams: {
        campaignId: campaignId,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setCampaignData(response.data.campaign);
  }

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    if (e.target.value.trim().length > 3) {
      navigate(
        `/brands/campaigns/${campaignId}/search?query=${e.target.value}`
      );
    }
  }

  function handleCloseSearch() {
    setSearch("");
    setSearchOpen(false);
    if (previousPath) {
      navigate(previousPath);
    } else {
      navigate(`/brands/campaigns/${campaignId}/metrics`);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (campaignId) getBrandCampaignById(campaignId);
    setSearchOpen(pathname.includes("search"));
  }, [campaignId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!pathname.includes("search") && pathname.includes(campaignId || "")) {
      setPreviousPath(pathname);
    }
  }, [pathname]);

  return (
    <div className="bg-white py-4 w-full h-full overflow-x-auto">
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center md:px-12 md:pt-5 pb-5 w-full">
          <p className="flex items-center gap-2 text-secondary">
            <Link to="/brands/campaigns/live">
              Campaigns
              </Link>
            <ChevronRight className="w-5 h-5 text-black" />
            <p className="text-black">
              {gettingBrandCampaign ? (
                <Spinner width={10} />
              ) : (
                campaignData?.name
              )}
            </p>
          </p>

          <div className="flex justify-between items-center px-2 border border-black rounded-xl w-full max-w-md overflow-hidden">
            {searchOpen ? (
              ""
            ) : (
              <ul className="flex justify-between items-center mt-5 md:mt-0 p-2 w-full font-semibold">
                {menuList.map((menu) => (
                  <Button
                    key={menu.link}
                    variant={isActive(menu.link) ? "yellow" : "secondary"}
                    to={menu.link}
                    onClick={() => setSearchOpen(false)}
                    className={`h-12 px-5 md:text-base text-xs ${isActive(menu.link) ? "" : "text-black bg-transparent border-none"}`}
                  >
                    {menu.name}
                  </Button>
                ))}
              </ul>
            )}

            <div
              className={`flex items-center ${searchOpen ? "w-full" : "bg-gray-100 rounded-lg"} h-16`}
            >
              <div className="relative flex items-center px-5 py-4 rounded-lg w-full h-full">
                {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setSearchOpen(true);
                    setPreviousPath(pathname);
                  }}
                >
                  <SearchIcon />
                </div>

                {searchOpen && (
                  <Input
                    value={search}
                    onChange={(e) => {
                      handleSearch(e);
                    }}
                    className="bg-transparent px-4 border-none w-full h-full font-medium text-base outline-none focus:ring-0"
                    placeholder="Search by name, instagram ID, and number"
                    autoFocus
                  />
                )}

                {searchOpen && (
                  <XIcon
                    onClick={handleCloseSearch}
                    className="right-5 absolute cursor-pointer"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <Outlet />
      </div>
    </div>
  );
}
